// Red-neon cursor trail.
// Desktop-only, opt-out via `disabled` prop. Renders a canvas overlay
// that paints a short fading trail behind the mouse with subtle smear.
// Designed to enhance the samurai/ronin pages without distracting.

import { useEffect, useRef } from "react";

export default function CursorTrail({
  disabled = false,
  color = "rgba(220,38,38,1)",
  glow  = "rgba(220,38,38,0.55)",
  maxPoints = 22,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (disabled) return;
    // Skip touch devices — cursor trail makes no sense without pointer
    const isTouch = window.matchMedia?.("(pointer: coarse)")?.matches;
    if (isTouch) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let w = 0, h = 0;
    const resize = () => {
      w = window.innerWidth;
      h = window.innerHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = w + "px";
      canvas.style.height = h + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener("resize", resize);

    const points = [];
    let mx = -1000, my = -1000;
    let rafId = 0;

    const onMove = (e) => {
      mx = e.clientX;
      my = e.clientY;
    };
    window.addEventListener("mousemove", onMove, { passive: true });

    const tick = () => {
      points.push({ x: mx, y: my, life: 1 });
      if (points.length > maxPoints) points.shift();

      ctx.clearRect(0, 0, w, h);
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        p.life *= 0.88;
        if (p.life < 0.04) continue;
        const t = i / (points.length || 1);
        const r = 2 + t * 5;
        // Outer glow
        ctx.beginPath();
        ctx.fillStyle = glow.replace(/,\s*[0-9.]+\)/, `, ${0.35 * p.life * t})`);
        ctx.arc(p.x, p.y, r * 2.5, 0, Math.PI * 2);
        ctx.fill();
        // Core dot
        ctx.beginPath();
        ctx.fillStyle = color.replace(/,\s*[0-9.]+\)/, `, ${0.9 * p.life * t})`);
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", resize);
    };
  }, [disabled, color, glow, maxPoints]);

  if (disabled) return null;

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 9999,
        mixBlendMode: "screen",
      }}
    />
  );
}
